import { PermissionGate } from "@/components/common/PermissionGate"
import { PERMISSIONS } from "@/constants/permissions"
import { useMemo } from "react"
import { useSearchParams } from "react-router"
import { EntityEmptyDetail } from "../../shared/EntityEmptyDetail"
import { SendInquiryPanel } from "../../shared/SendInquiryPanel"
import { ArticleActiveWorkSection } from "./ArticleActiveWorkSection"
import { ArticleAuditSection } from "./ArticleAuditSection"
import { ArticleBomSection } from "./ArticleBomSection"
import { ArticleComplianceSection } from "./ArticleComplianceSection"
import {
  ArticleFieldsBody,
  ArticleFieldsHeader,
  useArticleFieldsState,
} from "./ArticleFieldsSection"
import { ArticleProductGroupAssignSection } from "./ArticleProductGroupAssignSection"
import { ArticleProductsSection } from "./ArticleProductsSection"
import { ArticleSubstancesSection } from "./ArticleSubstancesSection"
import { ArticleSuppliersSection } from "./ArticleSuppliersSection"

interface ArticleDetailPanelProps {
  selectedIds: Set<string>
  onRemoveIds: (ids: string[]) => void
}

export function ArticleDetailPanel({
  selectedIds,
  onRemoveIds,
}: ArticleDetailPanelProps) {
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const fieldsState = useArticleFieldsState()
  // Memoized, not rebuilt per render: the bulk section derives a sorted key
  // and a query fingerprint from this array, and a selection can hold
  // thousands of ids.
  const articleIds = useMemo(() => [...selectedIds], [selectedIds])

  if (selectedIds.size > 0) {
    return (
      <div className="relative h-full">
        <SendInquiryPanel
          entityType="article"
          selectedIds={selectedIds}
          onRemoveIds={onRemoveIds}
          moreActions={
            <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
              <ArticleProductGroupAssignSection articleIds={articleIds} />
            </PermissionGate>
          }
        />
      </div>
    )
  }

  if (!id) return <EntityEmptyDetail body="MC_MD_EMPTY_SELECT_BODY_ARTICLE" />

  return (
    <div className="flex flex-col">
      <ArticleFieldsHeader state={fieldsState} />
      <ArticleActiveWorkSection />
      <ArticleComplianceSection />
      <ArticleSubstancesSection />
      <ArticleSuppliersSection />
      <ArticleProductsSection />
      <ArticleBomSection />
      <ArticleFieldsBody state={fieldsState} />
      <ArticleAuditSection />
    </div>
  )
}
